import React from 'react';
import { RecentActiveUser } from '@/services/admin/adminPrivilegedService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';

interface RecentActiveUsersTableProps {
  users: RecentActiveUser[];
}

const getActionDisplayName = (actionType: string): string => {
  const actionMap: Record<string, string> = {
    'recipe_created': 'Created Recipe',
    'recipe_updated': 'Updated Recipe', 
    'meal_planned': 'Planned Meal',
    'feedback_added': 'Added Feedback',
    'note_created': 'Added Note',
    'shopping_list_created': 'Created Shopping List',
    'shopping_list_updated': 'Updated Shopping List'
  };
  
  return actionMap[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getActionColor = (actionType: string): string => {
  const colorMap: Record<string, string> = {
    'recipe_created': 'bg-orange-100 text-orange-800',
    'recipe_updated': 'bg-orange-50 text-orange-700',
    'meal_planned': 'bg-purple-100 text-purple-800',
    'feedback_added': 'bg-blue-100 text-blue-800',
    'note_created': 'bg-green-100 text-green-800',
    'shopping_list_created': 'bg-yellow-100 text-yellow-800',
    'shopping_list_updated': 'bg-yellow-50 text-yellow-700'
  };
  
  return colorMap[actionType] || 'bg-gray-100 text-gray-800';
};

const RecentActiveUsersTable: React.FC<RecentActiveUsersTableProps> = ({ users }) => {
  if (!users || users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Recent Active Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            No recent activity found
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Recent Active Users
          <Badge variant="outline" className="text-xs">
            Last 5 Active
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 font-medium text-foreground">Name</th>
                <th className="text-left py-2 font-medium text-foreground">Registered</th>
                <th className="text-left py-2 font-medium text-foreground">Last Action</th>
                <th className="text-left py-2 font-medium text-foreground">When</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.userId} className="border-b border-border last:border-0">
                  <td className="py-3 font-medium text-foreground">
                    {user.firstName}
                  </td>
                  <td className="py-3 text-muted-foreground">
                    {formatDistanceToNow(new Date(user.registrationDate), { addSuffix: true })}
                  </td>
                  <td className="py-3">
                    <Badge 
                      variant="secondary" 
                      className={`${getActionColor(user.lastActionType)} text-xs`}
                    >
                      {getActionDisplayName(user.lastActionType)}
                    </Badge>
                  </td>
                  <td className="py-3 text-muted-foreground">
                    {formatDistanceToNow(new Date(user.lastActionDate), { addSuffix: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentActiveUsersTable;